import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { adminUpload } from "@/lib/adminUpload";
import { Plus, Pencil, Trash2, X, Loader2, Upload, Eye, EyeOff, LifeBuoy, Image as ImageIcon, GripVertical, Clock } from "lucide-react";
import { toast } from "sonner";
import MarkdownEditor from "@/components/MarkdownEditor";

const PARENTS = [
  { value: "", label: "(top-level)" },
  { value: "about", label: "About" },
  { value: "programs", label: "Programs" },
  { value: "new-to-islam", label: "New to Islam" },
  { value: "services", label: "Services" },
];

const blank = {
  slug: "", title: "", parent: "", body: "", cover_image: "", is_published: true, order: 0,
  cards: [], coming_soon: true,  // NEW pages default to "Coming soon" until admin fills them
  embed_form_url: "", embed_form_title: "",
  contact_name: "", contact_email: "", contact_phone: "",
};

export default function PagesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/pages/"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restoreDefaults = async () => {
    if (!window.confirm(
      "Restore the 15 default navigation pages (About / Programs / New to Islam)?\n\n" +
      "This is IDEMPOTENT — pages that already exist are kept (just force-published if hidden). " +
      "Missing pages are re-created from the original content.\n\nContinue?"
    )) return;
    setRestoring(true);
    try {
      const r = await api.post("/admin/maintenance/restore-default-pages", {}, { timeout: 60000 });
      const d = r.data;
      toast.success(`Pages restored — ${d.republished} republished, ${d.total_pages_live} live now.`);
      window.alert(
        `Default pages restored:\n` +
        `  • Republished (was hidden): ${d.republished}\n` +
        `  • Total live: ${d.total_pages_live}\n` +
        `  • Total in CMS: ${d.total_pages_all}\n\n` +
        `Per-slug actions:\n` +
        Object.entries(d.actions || {}).map(([k, v]) => `  • ${k}: ${v}`).join("\n")
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const startNew = () => setEditing({ ...blank });
  const startEdit = (it) => setEditing({ ...it });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fullUrl = await uploadReturn(file);
      setEditing((prev) => ({ ...prev, cover_image: fullUrl }));
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally { setUploading(false); }
  };

  // Shared: upload a file and return its absolute URL (used by CTA banner uploads too).
  const uploadReturn = adminUpload;

  const save = async () => {
    if (!editing.slug || !editing.title) return toast.error("Slug and title are required");
    if (!/^[a-z0-9-]+$/.test(editing.slug)) return toast.error("Slug must be lowercase letters, numbers, dashes");
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order || 0) };
      if (editing.id) {
        await api.put(`/admin/pages/${editing.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post("/admin/pages/", payload);
        toast.success("Created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this page permanently? This cannot be undone.\n\nTip: Use the 'Hide' button instead if you only want to remove it from public view.")) return;
    try { await api.delete(`/admin/pages/${id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleVisible = async (it) => {
    try {
      await api.put(`/admin/pages/${it.id}`, { ...it, is_published: !it.is_published });
      toast.success(!it.is_published ? "Page is now visible to public" : "Page hidden from public");
      await load();
    } catch { toast.error("Could not update visibility"); }
  };

  return (
    <div data-testid="admin-pages-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Manage</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Pages</h1>
          <p className="mt-2 text-ink/60 text-sm">Each page has rich-text content, optional cover image, and a public-visibility toggle. Pages with a parent appear in that section's nav dropdown.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={restoreDefaults}
            disabled={restoring}
            data-testid="pages-restore-defaults"
            title="Re-create the 15 default navigation pages and force-publish hidden ones"
            className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-gold-600 disabled:opacity-60 ring-2 ring-gold/40"
          >
            {restoring ? <Loader2 size={14} className="animate-spin" /> : <LifeBuoy size={14} />}
            Restore defaults
          </button>
          <button onClick={startNew} data-testid="pages-new-btn" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold hover:bg-navy-900">
            <Plus size={14} /> New page
          </button>
        </div>
      </div>

      {loading ? <div className="text-ink/40">Loading…</div> :
       items.length === 0 ? <div className="rounded-3xl bg-white border border-sand p-14 text-center text-ink/60">No pages yet.</div> : (
        <div className="rounded-3xl bg-white border border-sand admin-scroll admin-scroll-hint">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-sand/50 text-left">
              <tr>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Title</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Slug</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Section</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-sand hover:bg-cream/60">
                  <td className="px-5 py-3 text-ink font-medium">{it.title}</td>
                  <td className="px-5 py-3 text-ink/65 text-xs"><code className="bg-sand/40 px-1.5 py-0.5 rounded">/{it.slug}</code></td>
                  <td className="px-5 py-3 text-ink/70 capitalize">{it.parent || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase font-semibold rounded-full px-2.5 py-1 ${it.is_published ? "bg-navy/10 text-navy" : "bg-sand text-ink/50"}`}>
                      {it.is_published ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => toggleVisible(it)}
                      title={it.is_published ? "Hide from public" : "Show to public"}
                      data-testid={`pg-toggle-${it.slug}`}
                      className="p-2 text-ink/60 hover:text-navy"
                    >
                      {it.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button onClick={() => startEdit(it)} title="Edit" data-testid={`pg-edit-${it.slug}`} className="p-2 text-ink/60 hover:text-navy"><Pencil size={14} /></button>
                    <button onClick={() => del(it.id)} title="Delete permanently" data-testid={`pg-delete-${it.slug}`} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white border border-sand p-7 md:p-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-ink">{editing.id ? "Edit page" : "New page"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 text-ink/60 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Page title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="pg-title" />
                <Field label="Slug *  (e.g. history-mission)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testId="pg-slug" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Parent section</label>
                  <select
                    value={editing.parent || ""}
                    onChange={(e) => setEditing({ ...editing, parent: e.target.value })}
                    data-testid="pg-parent"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy bg-white"
                  >
                    {PARENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                  <div className="text-[10px] text-ink/50 mt-1">Determines which dropdown menu the page appears under.</div>
                </div>
                <Field label="Order in section (lower = first)" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testId="pg-order" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Cover image (optional)</label>
                <div className="mt-2 flex flex-wrap items-start gap-4">
                  {editing.cover_image && (
                    <img src={editing.cover_image} alt="cover" className="w-32 h-20 object-cover rounded-xl border border-sand" />
                  )}
                  <div className="flex-1 min-w-[220px]">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => upload(e.target.files?.[0])}
                      className="hidden"
                      data-testid="pg-cover-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="pg-cover-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold hover:bg-navy-900 disabled:opacity-60"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {editing.cover_image ? "Replace cover" : "Upload cover"}
                    </button>
                    <input
                      type="text"
                      value={editing.cover_image || ""}
                      onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                      placeholder="Or paste an image URL…"
                      data-testid="pg-cover-url"
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-sand text-xs focus:outline-none focus:border-navy"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Page content (rich text — supports formatting, images, links)</label>
                <div className="mt-2">
                  <MarkdownEditor
                    value={editing.body || ""}
                    onChange={(v) => setEditing({ ...editing, body: v })}
                    height={360}
                    testIdPrefix="pg-body"
                  />
                </div>
              </div>

              <CtaButtonsEditor
                items={editing.cta_buttons || []}
                onChange={(v) => setEditing({ ...editing, cta_buttons: v })}
                onUpload={uploadReturn}
              />

              <CardsEditor
                items={editing.cards || []}
                onChange={(v) => setEditing({ ...editing, cards: v })}
                onUpload={uploadReturn}
              />

              {/* Embedded form (Jotform / Google Forms / Typeform / generic iframe) */}
              <div className="rounded-2xl border border-sand p-5 bg-cream/30 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
                    Embedded form URL <span className="text-ink/40 normal-case tracking-normal">(optional)</span>
                  </label>
                  <div className="text-xs text-ink/55 mt-1">
                    Paste a Jotform, Google Forms, Typeform, or Microsoft Forms link. The form will be embedded directly on this page —
                    visitors stay on flintislamiccenter.com and never feel redirected. Jotform forms resize automatically as the user fills them in.
                  </div>
                  <input
                    type="url"
                    value={editing.embed_form_url || ""}
                    onChange={(e) => setEditing({ ...editing, embed_form_url: e.target.value })}
                    placeholder="https://form.jotform.com/2510123456789  or  https://docs.google.com/forms/d/e/…/viewform"
                    data-testid="pg-embed-url"
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy text-sm font-mono"
                  />
                </div>
                {editing.embed_form_url && (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
                      Form section heading <span className="text-ink/40 normal-case tracking-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={editing.embed_form_title || ""}
                      onChange={(e) => setEditing({ ...editing, embed_form_title: e.target.value })}
                      placeholder="Registration (default)"
                      data-testid="pg-embed-title"
                      className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Per-page contact override (responsible person for this page) */}
              <div className="rounded-2xl border border-sand p-5 bg-cream/30 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
                    Page contact <span className="text-ink/40 normal-case tracking-normal">(optional — leave blank to use site defaults)</span>
                  </label>
                  <div className="text-xs text-ink/55 mt-1">
                    Visitors with questions about this specific page will be directed to this person.
                    Otherwise they're sent to the main FIC contact form & the masjid's phone number.
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={editing.contact_name || ""}
                    onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })}
                    placeholder="Coordinator name (e.g. Br. Yusuf)"
                    data-testid="pg-contact-name"
                    className="px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy text-sm"
                  />
                  <input
                    type="email"
                    value={editing.contact_email || ""}
                    onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="pg-contact-email"
                    className="px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy text-sm"
                  />
                  <input
                    type="tel"
                    value={editing.contact_phone || ""}
                    onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })}
                    placeholder="(810) 555-0100"
                    data-testid="pg-contact-phone"
                    className="px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy text-sm"
                  />
                </div>
                <div className="text-[11px] text-ink/45">
                  When email is set, the page's "Contact" button becomes a direct mailto: link with a subject pre-filled.
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-3 cursor-pointer py-2">
                  <input
                    type="checkbox"
                    checked={!!editing.is_published}
                    onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                    data-testid="pg-published"
                  />
                  <span className="text-sm text-ink/75">Visible on public site</span>
                </label>
                <label className="inline-flex items-center gap-3 cursor-pointer py-2 px-3 rounded-xl bg-amber-50 border border-amber-200">
                  <input
                    type="checkbox"
                    checked={!!editing.coming_soon}
                    onChange={(e) => setEditing({ ...editing, coming_soon: e.target.checked })}
                    data-testid="pg-coming-soon"
                  />
                  <span className="text-sm text-amber-900 inline-flex items-center gap-1.5">
                    <Clock size={13} /> Show "Coming soon" landing page (overrides body until you uncheck)
                  </span>
                </label>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-full px-6 py-2.5 text-sm font-medium text-ink/70 hover:bg-sand/50">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="pg-save"
                      className="rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900 disabled:opacity-60 inline-flex items-center gap-2">
                {saving && <Loader2 size={14} className="animate-spin" />} Save page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
      />
    </div>
  );
}


function CtaButtonsEditor({ items, onChange, onUpload }) {
  const add = () => {
    if (items.length >= 3) return toast.error("Max 3 buttons per page");
    onChange([...items, { label: "", url: "", banner_image: "", style: "primary" }]);
  };
  const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const uploadBanner = async (i, file) => {
    if (!file) return;
    try { const url = await onUpload(file); update(i, { banner_image: url }); toast.success("Banner uploaded"); }
    catch (e) { toast.error(e.message || "Upload failed"); }
  };

  return (
    <div className="rounded-2xl border border-sand p-5 bg-cream/30">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Call-to-action buttons (up to 3)</label>
          <div className="text-xs text-ink/55 mt-0.5">Shown at the bottom of the page. Add an optional banner image above each button.</div>
        </div>
        <button type="button" onClick={add} data-testid="cta-add" className="inline-flex items-center gap-1 rounded-full bg-gold/20 hover:bg-gold/35 text-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider">
          <Plus size={12} /> Add button
        </button>
      </div>

      {items.length === 0 && <div className="mt-4 text-xs text-ink/50 italic">No buttons yet.</div>}

      <div className="space-y-4 mt-4">
        {items.map((b, i) => (
          <div key={i} data-testid={`cta-row-${i}`} className="rounded-xl bg-white border border-sand p-4 space-y-3">
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0 space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <input
                    type="text" value={b.label || ""} onChange={(e) => update(i, { label: e.target.value })}
                    placeholder="Button label (e.g. Book the Hall)" data-testid={`cta-label-${i}`}
                    className="px-4 py-2.5 rounded-lg border border-sand focus:outline-none focus:border-navy text-sm"
                  />
                  <input
                    type="text" value={b.url || ""} onChange={(e) => update(i, { url: e.target.value })}
                    placeholder="URL (/donate or https://…)" data-testid={`cta-url-${i}`}
                    className="px-4 py-2.5 rounded-lg border border-sand focus:outline-none focus:border-navy text-sm"
                  />
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={b.style || "primary"} onChange={(e) => update(i, { style: e.target.value })}
                    data-testid={`cta-style-${i}`}
                    className="px-3 py-2 rounded-lg border border-sand text-xs bg-white"
                  >
                    <option value="primary">Navy (primary)</option>
                    <option value="gold">Gold (emphasis)</option>
                    <option value="secondary">Outline (secondary)</option>
                  </select>
                  <label className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-3 py-1.5 text-xs font-semibold cursor-pointer hover:bg-navy-900">
                    <Upload size={10} /> {b.banner_image ? "Replace banner" : "Upload banner (optional)"}
                    <input type="file" accept="image/*" className="hidden"
                      data-testid={`cta-banner-input-${i}`}
                      onChange={(e) => uploadBanner(i, e.target.files?.[0])}
                    />
                  </label>
                  {b.banner_image && (
                    <button type="button" onClick={() => update(i, { banner_image: "" })} className="text-xs text-ink/55 hover:text-red-600 underline">Remove banner</button>
                  )}
                </div>
                {b.banner_image && (
                  <img src={b.banner_image} alt="banner preview" className="w-full max-h-32 object-cover rounded-lg border border-sand" />
                )}
              </div>
              <button type="button" onClick={() => remove(i)} data-testid={`cta-remove-${i}`} className="p-2 text-red-500 hover:bg-red-50 rounded-md shrink-0"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * CardsEditor — admins compose visual cards (image + heading + paragraph + optional link)
 * that render on the public page in a center-out grid (first card centered,
 * later cards expand outward). Up to 9 cards per page.
 */
function CardsEditor({ items, onChange, onUpload }) {
  const add = () => {
    if (items.length >= 9) return toast.error("Max 9 cards per page");
    onChange([...items, { image: "", title: "", body: "", link_url: "", link_label: "" }]);
  };
  const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const uploadCardImage = async (i, file) => {
    if (!file) return;
    try {
      const url = await onUpload(file);
      update(i, { image: url });
      toast.success("Card image uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    }
  };

  return (
    <div className="rounded-2xl border border-sand p-5 bg-cream/30">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Visual cards (image + text, up to 9)</label>
          <div className="text-xs text-ink/55 mt-0.5">Cards render in a center-out grid below the page body. First card sits in the middle on 3-column layouts; subsequent cards expand outward.</div>
        </div>
        <button type="button" onClick={add} data-testid="card-add" className="inline-flex items-center gap-1 rounded-full bg-gold/20 hover:bg-gold/35 text-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider">
          <Plus size={12} /> Add card
        </button>
      </div>

      {items.length === 0 && <div className="mt-4 text-xs text-ink/50 italic">No cards yet — click "Add card" to create the first one.</div>}

      <div className="space-y-4 mt-4">
        {items.map((c, i) => (
          <div key={i} data-testid={`card-row-${i}`} className="rounded-xl bg-white border border-sand p-4">
            <div className="flex items-start gap-4">
              {/* Reorder grip */}
              <div className="flex flex-col items-center gap-1 pt-2 shrink-0">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  data-testid={`card-up-${i}`}
                  className="p-1 text-ink/40 hover:text-navy disabled:opacity-30"
                  title="Move up"
                >▲</button>
                <GripVertical size={14} className="text-ink/30" />
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  data-testid={`card-down-${i}`}
                  className="p-1 text-ink/40 hover:text-navy disabled:opacity-30"
                  title="Move down"
                >▼</button>
              </div>

              {/* Image preview / uploader */}
              <div className="shrink-0">
                <label className="block w-32 h-24 rounded-lg overflow-hidden border-2 border-dashed border-sand bg-cream cursor-pointer hover:border-navy transition-colors relative group">
                  {c.image ? (
                    <img src={c.image} alt="card preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-ink/40">
                      <ImageIcon size={20} />
                      <span className="text-[9px] uppercase tracking-wider mt-1">Add image</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`card-image-input-${i}`}
                    onChange={(e) => uploadCardImage(i, e.target.files?.[0])}
                  />
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Upload size={16} className="text-cream" />
                  </div>
                </label>
                {c.image && (
                  <button type="button" onClick={() => update(i, { image: "" })} className="block w-full mt-1.5 text-[10px] text-ink/55 hover:text-red-600 underline">Remove image</button>
                )}
              </div>

              {/* Card text fields */}
              <div className="flex-1 min-w-0 space-y-2">
                <input
                  type="text"
                  value={c.title || ""}
                  onChange={(e) => update(i, { title: e.target.value })}
                  placeholder="Card title (e.g. Weekend School)"
                  data-testid={`card-title-${i}`}
                  className="w-full px-3 py-2 rounded-lg border border-sand focus:outline-none focus:border-navy text-sm font-semibold"
                />
                <textarea
                  value={c.body || ""}
                  onChange={(e) => update(i, { body: e.target.value })}
                  placeholder="Short description (1–4 sentences)"
                  data-testid={`card-body-${i}`}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-sand focus:outline-none focus:border-navy text-sm resize-y"
                />
                <div className="grid md:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={c.link_label || ""}
                    onChange={(e) => update(i, { link_label: e.target.value })}
                    placeholder="Link label (optional, e.g. Learn more)"
                    data-testid={`card-link-label-${i}`}
                    className="px-3 py-2 rounded-lg border border-sand focus:outline-none focus:border-navy text-xs"
                  />
                  <input
                    type="text"
                    value={c.link_url || ""}
                    onChange={(e) => update(i, { link_url: e.target.value })}
                    placeholder="Link URL (optional, /programs or https://…)"
                    data-testid={`card-link-url-${i}`}
                    className="px-3 py-2 rounded-lg border border-sand focus:outline-none focus:border-navy text-xs"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`card-remove-${i}`}
                className="p-2 text-red-500 hover:bg-red-50 rounded-md shrink-0"
                title="Remove card"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
